import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
} from '@angular/core';

/**
 * Styles the goa-container shadow DOM: hides the always-rendered (and empty,
 * when no heading slot is supplied) header and restores the full border/radius
 * that the `accent` variants strip from the content box. Neither is exposed as
 * a CSS token, so a stylesheet is injected into the shadow root where it
 * survives re-renders.
 */
@Directive({
  selector: '[containerShadowStyle]',
  standalone: true,
})
export class ContainerShadowStyleDirective implements AfterViewInit {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly platformId = inject(PLATFORM_ID);

  hideHeader = input(true);
  border = input('var(--goa-container-border)');
  borderRadius = input('var(--goa-container-border-radius)');

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.applyStyles();
    }
  }

  private applyStyles(retries = 10): void {
    const host = this.el.nativeElement;
    const container = host.matches('goa-container')
      ? host
      : host.querySelector<HTMLElement>('goa-container');
    const shadow = container?.shadowRoot;

    if (!shadow || typeof shadow.appendChild !== 'function') {
      if (retries > 0) {
        requestAnimationFrame(() => this.applyStyles(retries - 1));
      }
      return;
    }

    const shadowRoots = new Set<ShadowRoot>([shadow]);
    for (const element of Array.from(shadow.querySelectorAll('*'))) {
      if (element.shadowRoot) {
        shadowRoots.add(element.shadowRoot);
      }
    }

    for (const styleRoot of shadowRoots) {
      const rules: string[] = [];
      if (this.hideHeader()) {
        rules.push('header { display: none !important; }');
      }
      rules.push(
        `.content { border: ${this.border()} !important; border-radius: ${this.borderRadius()} !important; }`
      );

      let style = styleRoot.querySelector<HTMLStyleElement>(
        'style#container-shadow-style'
      );
      if (!style) {
        style = document.createElement('style');
        style.id = 'container-shadow-style';
        styleRoot.appendChild(style);
      }
      style.textContent = rules.join('\n');
    }
  }
}
